import { h } from "preact";
import { RoutableProps } from "preact-router";
import { useEffect, useState } from "preact/hooks";
import { Label, ProductTypeRevision } from "../../api/product-api";
import { Snackbar, SnackbarShowCommand } from "../../components/Snackbar";
import { useCommand, useStepperIndex } from "../../hooks";
import "./styles.scss";
import { LabelPrint } from "../../components/LabelPrint";
import { RevisionSelect } from "../../components/RevisionSelect";
import { LabelSelect } from "../../components/LabelSelect";
import { useRevisions } from "../../hooks/useRevisions";

export function PrintSerialNumber(props: RoutableProps & { type?: string }) {
    const { manufacturingConfiguration, revisions, currentRevision } = useRevisions(props.type ?? "");

    const [selectedLabel, setSelectedLabel] = useState(null as null | Label);
    const refreshPrinter = useCommand();
    const [batchCount, setBatchCount] = useState("1");
    const [eachCopyCount, setEachCopyCount] = useState("1");

    const showSnackbarCommand = useCommand<SnackbarShowCommand>();
    const stepperIndex = useStepperIndex();
    const [selectedRevision, setSelectedRevision] = useState(null as ProductTypeRevision | null);

    useEffect(() => {
        if (currentRevision) {
            setSelectedRevision(currentRevision);
        }
    }, [currentRevision]);

    useEffect(() => {
        refreshPrinter.run();
    }, []);

    useEffect(() => {
        return () => {
            sessionStorage.setItem(`last-serial-number-${props.type}`, "");
        };
    });

    useEffect(() => {
        if (Math.floor(+batchCount) * Math.floor(+eachCopyCount) > 100) {
            let newBatchCount = Math.floor(+batchCount);

            while (newBatchCount > 1 && newBatchCount * Math.floor(+eachCopyCount) > 100) {
                newBatchCount--;
            }

            setBatchCount(newBatchCount.toString());

            showSnackbarCommand.run(
                "Maksymalna ilość naklejek jaką można wydrukować na raz to 100",
                "error",
                "error",
                3000,
            );
        }
    }, [batchCount]);

    useEffect(() => {
        if (Math.floor(+batchCount) * Math.floor(+eachCopyCount) > 100) {
            let newEachCopyCount = Math.floor(+eachCopyCount);

            while (newEachCopyCount > 1 && Math.floor(+batchCount) * newEachCopyCount > 100) {
                newEachCopyCount--;
            }

            setEachCopyCount(newEachCopyCount.toString());

            showSnackbarCommand.run(
                "Maksymalna ilość naklejek jaką można wydrukować na raz to 100",
                "error",
                "error",
                3000,
            );
        }
    }, [eachCopyCount]);

    if (!manufacturingConfiguration) {
        return null;
    }

    return (
        <div class="generate-serial-number-page">
            <h1>{manufacturingConfiguration.productType.name}</h1>
            <div>{manufacturingConfiguration.productType.brand}</div>
            {revisions.length <= 1
                ? null
                : [
                      <div class="stepper">
                          <div class="steps">
                              <div
                                  class={"step" + (selectedRevision ? " done success" : " current")}
                                  data-index={stepperIndex()}
                              >
                                  Wybierz rewizję
                              </div>
                          </div>
                      </div>,
                      <div class="paper" style="padding: 10px; margin-top: 10px;">
                          <h1>Rewizje</h1>
                          <RevisionSelect
                              currentRevision={manufacturingConfiguration.productType.currentProductTypeRevision}
                              revisions={revisions}
                              selected={selectedRevision}
                              onChange={v => {
                                  setSelectedRevision(v);
                                  setSelectedLabel(null);
                              }}
                          />
                      </div>,
                  ]}
            {selectedRevision
                ? [
                      <div class="stepper">
                          <div class="steps">
                              <div
                                  class={"step" + (selectedLabel ? " done success" : " current")}
                                  data-index={stepperIndex()}
                              >
                                  Wybierz naklejkę
                              </div>
                          </div>
                      </div>,
                      <div class="paper" style="padding: 10px; margin-top: 10px; margin-bottom: 10px">
                          <h1>Naklejki</h1>
                          <LabelSelect
                              autoSelect
                              revision={selectedRevision}
                              selected={selectedLabel}
                              onChange={setSelectedLabel}
                          />
                      </div>,
                  ]
                : null}
            {!selectedLabel
                ? null
                : [
                      [
                          <div class="stepper">
                              <div class="steps">
                                  <div
                                      class={"step" + (batchCount ? " done success" : " current")}
                                      data-index={stepperIndex()}
                                  >
                                      Podaj ilość kodów seryjnych do wygenerowania
                                  </div>
                              </div>
                          </div>,
                          <div class="create-serial">
                              <input
                                  type="number"
                                  value={batchCount}
                                  onChange={e => setBatchCount(e.currentTarget.value)}
                              />
                          </div>,
                          <div class="stepper">
                              <div class="steps">
                                  <div
                                      class={"step" + (eachCopyCount ? " done success" : " current")}
                                      data-index={stepperIndex()}
                                  >
                                      Podaj ilość kopii każdego kodu seryjnego (każda kopia będzie miała ten sam numer
                                      seryjny)
                                  </div>
                              </div>
                          </div>,
                          <div class="create-serial">
                              <input
                                  type="number"
                                  value={eachCopyCount}
                                  onChange={e => setEachCopyCount(e.currentTarget.value)}
                              />
                          </div>,
                      ],
                      batchCount && eachCopyCount && selectedRevision
                          ? [
                                <div class="stepper">
                                    <div class="steps">
                                        <div class="step current" data-index={stepperIndex()}>
                                            Wydrukuj naklejki (łączna ilość:{" "}
                                            {parseInt(batchCount, 10) * parseInt(eachCopyCount, 10)})
                                        </div>
                                    </div>
                                </div>,
                                <div class="print paper mb-2">
                                    <h1>Drukowanie naklejek</h1>
                                    <LabelPrint
                                        label={selectedLabel}
                                        copies={parseInt(batchCount, 10)}
                                        noAutoCut={true}
                                        doRefresh={refreshPrinter}
                                        eachProvideFieldsCopy={parseInt(eachCopyCount, 10)}
                                        productTypeRevision={selectedRevision}
                                    />
                                </div>,
                                // manufacturingConfiguration.otherData?.labelingStepsPreview ? (
                                //     <div class="paper" style="margin: 10px 0px; padding: 10px;">
                                //         <h1>Przkład naklejania</h1>
                                //         <div style="display: flex; justify-content: center;">
                                //             <img
                                //                 src={resolvePath(
                                //                     manufacturingConfiguration.otherData?.labelingStepsPreview,
                                //                 )}
                                //             />
                                //         </div>
                                //     </div>
                                // ) : null,
                            ]
                          : null,
                  ]}
            <Snackbar doShow={showSnackbarCommand} />
        </div>
    );
}
